import { useState, useRef, useEffect, DragEvent, ChangeEvent } from "react";

export const useFileUpload = () => {
    const [files, setFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [fileSizeOverLimit, setFileSizeOverLimit] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    const MAX_FILE_SIZE = 21 * 1024 * 1024;

    const handleFiles = (newFiles: FileList | null) => {
        if (newFiles) {
            setFiles(prevFiles => [...prevFiles, ...Array.from(newFiles)]);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const droppedFiles = e.dataTransfer.files;
        handleFiles(droppedFiles);
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        fileInputRef.current?.click();
    };

    const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
    };

    const handleRemoveFile = (index: number) => {
        setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    useEffect(() => {
        const totalSize = files.reduce((sum, file) => sum + file.size, 0);
        setFileSizeOverLimit(totalSize > MAX_FILE_SIZE);
    }, [files]);

    return {
        files,
        isDragging,
        fileSizeOverLimit,
        fileInputRef,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop,
        handleClick,
        handleButtonClick,
        handleFileInputChange,
        handleRemoveFile,
    };
};
